/**
 * product-resources.js
 *
 * Click-to-play + two-axis filter behavior for the Product Resources page.
 * Also drives the product-page mirror (product-videos section) via the
 * same [data-section-type="product-resources"] init path.
 *
 * Features:
 *  - Click-to-play: swaps thumbnail for YouTube/Vimeo iframe on first click
 *  - Two-axis filter chips: type (video/manual/spec-sheet/brochure/cad-drawing)
 *    and family (belt/tube/disk/coolant skimmers). Filters AND-combine.
 *    Toggles [hidden] on non-matching cards and syncs URL hash as
 *    #type=<value>&family=<value>.
 *  - popstate: browser back/forward navigates filter states
 *  - IntersectionObserver: staggered card reveal (gated on reduced-motion)
 *  - GA4 events: video_play, filter_interaction, select_content
 *
 * @version 2.0.0
 */
(function () {
  'use strict';

  var ALLOWED_TYPES = ['all', 'video', 'manual', 'spec-sheet', 'brochure', 'cad-drawing', 'other'];
  var ALLOWED_FAMILIES = ['all', 'belt-skimmers', 'tube-skimmers', 'disk-skimmers', 'coolant-recyclers', 'other'];

  class ProductResources {
    constructor(section) {
      this.section = section;
      this.typeFilters = Array.prototype.slice.call(section.querySelectorAll('[data-product-resources-filter-axis="type"]'));
      this.familyFilters = Array.prototype.slice.call(section.querySelectorAll('[data-product-resources-filter-axis="family"]'));
      this.cards = Array.prototype.slice.call(section.querySelectorAll('[data-resource-card]'));
      this.announcer = section.querySelector('[data-product-resources-announce]');
      this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeType = 'all';
      this.activeFamily = 'all';

      this._bindPlay();
      this._bindShopClicks();
      this._bindDownloads();
      if (this.typeFilters.length || this.familyFilters.length) {
        this._bindFilters();
        this._syncFromHash();
      }
      this._revealCards();
    }

    _bindDownloads() {
      this.section.addEventListener('click', (e) => {
        const link = e.target.closest('[data-resources-download]');
        if (!link) return;
        const fileName = link.dataset.fileName || '';
        const fileExt = link.dataset.fileExt || '';
        const url = link.getAttribute('href') || '';
        // Read the product handle from the closest [data-product-handle]
        // ancestor — the per-product section root emits this. Falls back
        // to parsing /products/<handle>/ from the URL when on a product
        // page but the attribute isn't present (e.g., compact mirror in
        // some PageFly product builds).
        const handleHost = link.closest('[data-product-handle]');
        let productHandle = handleHost ? (handleHost.dataset.productHandle || '') : '';
        if (!productHandle && document.body.classList.contains('template-product')) {
          const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
          if (m) productHandle = m[1];
        }
        const source = document.body.classList.contains('template-product') ? 'product_page' : 'resources_hub';
        this._trackEvent('file_download', {
          file_name: fileName,
          file_extension: fileExt.toLowerCase(),
          link_url: url,
          product_handle: productHandle || null,
          source: source
        });
      });
    }

    _bindPlay() {
      this.section.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-resource-play]');
        if (!btn) return;
        e.preventDefault();
        const player = btn.closest('[data-resource-player]');
        if (!player || player.dataset.swapped === 'true') return;
        this._swapToIframe(player);
      });
    }

    _swapToIframe(player) {
      const id = player.dataset.videoId || '';
      const provider = player.dataset.provider || 'youtube';
      const title = player.dataset.title || 'Video';
      if (!id) return;

      let src = '';
      if (provider === 'vimeo') {
        src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1';
      } else {
        src = 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
      }

      const iframe = document.createElement('iframe');
      iframe.className = 'resource-card__iframe';
      iframe.src = src;
      iframe.title = title;
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('loading', 'lazy');

      player.innerHTML = '';
      player.appendChild(iframe);
      player.dataset.swapped = 'true';

      const card = player.closest('[data-resource-card]');
      const category = card ? card.dataset.category : '';
      const productHandle = card ? card.dataset.productHandle : '';
      this._trackEvent('video_play', {
        video_title: title,
        video_category: category,
        linked_product_handle: productHandle || null,
        page_location: window.location.href
      });
    }

    _bindShopClicks() {
      this.section.addEventListener('click', (e) => {
        const link = e.target.closest('[data-resource-shop]');
        if (!link) return;
        const handle = link.dataset.productHandle || '';
        this._trackEvent('select_content', {
          content_type: 'video_card',
          item_id: handle
        });
      });
    }

    _bindFilters() {
      this.typeFilters.forEach((chip) => {
        chip.addEventListener('click', () => {
          const value = chip.dataset.productResourcesFilter || 'all';
          this._apply({ type: value, family: this.activeFamily }, { updateHash: true, source: 'click' });
        });
      });
      this.familyFilters.forEach((chip) => {
        chip.addEventListener('click', () => {
          const value = chip.dataset.productResourcesFilter || 'all';
          this._apply({ type: this.activeType, family: value }, { updateHash: true, source: 'click' });
        });
      });
      window.addEventListener('popstate', () => {
        this._syncFromHash();
      });
    }

    _syncFromHash() {
      const hash = (window.location.hash || '').replace(/^#/, '');
      const params = new URLSearchParams(hash);
      const rawType = (params.get('type') || 'all').toLowerCase();
      const rawFamily = (params.get('family') || 'all').toLowerCase();
      const type = ALLOWED_TYPES.indexOf(rawType) !== -1 ? rawType : 'all';
      const family = ALLOWED_FAMILIES.indexOf(rawFamily) !== -1 ? rawFamily : 'all';
      this._apply({ type: type, family: family }, { updateHash: false, source: 'hash' });
    }

    _apply(state, opts) {
      opts = opts || {};
      const type = ALLOWED_TYPES.indexOf(state.type) !== -1 ? state.type : 'all';
      const family = ALLOWED_FAMILIES.indexOf(state.family) !== -1 ? state.family : 'all';
      const changed = type !== this.activeType || family !== this.activeFamily;
      this.activeType = type;
      this.activeFamily = family;

      // Update aria-pressed on both axis rows
      this.typeFilters.forEach((c) => {
        c.setAttribute('aria-pressed', (c.dataset.productResourcesFilter || 'all') === type ? 'true' : 'false');
      });
      this.familyFilters.forEach((c) => {
        c.setAttribute('aria-pressed', (c.dataset.productResourcesFilter || 'all') === family ? 'true' : 'false');
      });

      // AND filter on both axes
      let visibleCount = 0;
      this.cards.forEach((card) => {
        const cardType = card.dataset.type || 'other'; // "video" or "attachment"
        let typeMatch = type === 'all';
        if (!typeMatch) {
          if (type === 'video') {
            typeMatch = cardType === 'video';
          } else {
            // Doc subtype filter — applies to attachments only, matches data-category
            typeMatch = cardType === 'attachment' && (card.dataset.category || 'other') === type;
          }
        }
        const familyMatch = family === 'all' || (card.dataset.family || 'other') === family;
        const show = typeMatch && familyMatch;

        // Hide the wrapping <li> (when gridded) rather than the article, so the
        // CSS grid reflows instead of leaving empty cells. Falls back to the
        // card itself on the product-page mirror where there is no <li>.
        const target = card.closest('li') || card;
        if (show) {
          target.removeAttribute('hidden');
          visibleCount++;
        } else {
          target.setAttribute('hidden', '');
        }
      });

      // Only push a new history entry when the filter actually changes —
      // otherwise repeated clicks on the active chip stack duplicate entries
      // and back/forward navigation feels broken.
      if (opts.updateHash && changed) {
        const parts = [];
        if (type   !== 'all') parts.push('type=' + encodeURIComponent(type));
        if (family !== 'all') parts.push('family=' + encodeURIComponent(family));
        const newHash = parts.length ? '#' + parts.join('&') : '';
        const newUrl = window.location.pathname + window.location.search + newHash;
        history.pushState({ type: type, family: family }, '', newUrl);
      }

      // Only announce on user-initiated changes. Announcing on initial sync or
      // popstate would read the status to screen readers on page load / back
      // navigation, which is noisy and unexpected.
      if (opts.source === 'click') {
        if (this.announcer) {
          // Clear then set on next frame so repeat clicks re-trigger announcement
          // (polite aria-live regions otherwise skip identical textContent).
          // Matches the pattern used in assets/product-parts.js.
          const message = visibleCount + ' resource' + (visibleCount === 1 ? '' : 's') + ' shown';
          this.announcer.textContent = '';
          requestAnimationFrame(() => { this.announcer.textContent = message; });
        }
        this._trackEvent('filter_interaction', {
          filter_type: 'resources_two_axis',
          filter_value: type,
          filter_family: family
        });
      }
    }

    _revealCards() {
      if (this.reduceMotion || !('IntersectionObserver' in window)) {
        this.cards.forEach((c) => c.classList.add('is-revealed'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            setTimeout(() => el.classList.add('is-revealed'), i * 60);
            io.unobserve(el);
          }
        });
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0.1 });
      this.cards.forEach((c) => io.observe(c));
    }

    _trackEvent(name, params) {
      if (typeof window.gtag === 'function') {
        try { window.gtag('event', name, params || {}); } catch (_) { /* noop */ }
      } else if (window.dataLayer) {
        try { window.dataLayer.push(Object.assign({ event: name }, params || {})); } catch (_) { /* noop */ }
      }
    }
  }

  function initProductResourcesSections() {
    document.querySelectorAll('[data-section-type="product-resources"]').forEach((el) => {
      if (el.dataset.productResourcesInitialized === 'true') return;
      el.dataset.productResourcesInitialized = 'true';
      new ProductResources(el);
    });
  }

  // Guard against the script being included by more than one section on the
  // same page, which would otherwise attach duplicate DOMContentLoaded
  // listeners and double-bind events.
  if (!window.__productResourcesInit) {
    window.__productResourcesInit = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initProductResourcesSections);
    } else {
      initProductResourcesSections();
    }
  }
})();
